/**
 * SessionsPage — Full-page OpenChamber iframe embedded in the AJNT dashboard.
 *
 * State machine:
 *   checking → active | sleeping | waking | creating | none | error
 *
 * - active   → renders full-height iframe to https://oc.ajnt.ai
 * - sleeping → shows "Starting workspace…" UI, triggers wake, polls until ready
 * - waking / creating → shows spinner + polls sandbox status every 3s
 * - none     → shows setup/create prompt with launch button
 * - error    → shows error message with retry
 */
import { useEffect, useRef, useCallback } from "react";
import { Server, RefreshCw, Rocket, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useSandboxStore } from "@/stores/useSandboxStore";

const OC_URL = "https://oc.ajnt.ai";
const POLL_INTERVAL_MS = 3_000;

/** Sandbox states that mean "still booting — keep polling" */
const TRANSITIONAL_STATES = new Set(["creating", "waking", "provisioning"]);

export function SessionsPage() {
  const {
    sandbox,
    loading,
    error,
    fetchSandboxes,
    fetchSandboxStatus,
    wakeSandbox,
    launchSandbox,
    clearError,
  } = useSandboxStore();

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transitionStartRef = useRef<number | null>(null);

  /* ------------------------------------------------------------------ */
  /*  Initial load                                                       */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    fetchSandboxes();
  }, [fetchSandboxes]);

  /* ------------------------------------------------------------------ */
  /*  Polling for transitional states                                    */
  /* ------------------------------------------------------------------ */

  const startPolling = useCallback(() => {
    if (pollRef.current) return; // already polling
    pollRef.current = setInterval(() => {
      const sb = useSandboxStore.getState().sandbox;
      if (!sb || !TRANSITIONAL_STATES.has(sb.state)) {
        // Stop polling once we leave a transitional state
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        return;
      }
      fetchSandboxStatus(sb.id);
    }, POLL_INTERVAL_MS);
  }, [fetchSandboxStatus]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Auto-start polling when sandbox is in a transitional state
  useEffect(() => {
    if (sandbox && TRANSITIONAL_STATES.has(sandbox.state)) {
      if (!transitionStartRef.current) {
        transitionStartRef.current = Date.now();
      }
      startPolling();
    } else {
      transitionStartRef.current = null;
      stopPolling();
    }
    return stopPolling;
  }, [sandbox?.state, startPolling, stopPolling]);

  /* ------------------------------------------------------------------ */
  /*  Actions                                                            */
  /* ------------------------------------------------------------------ */

  const handleWake = async () => {
    if (!sandbox) return;
    clearError();
    await wakeSandbox(sandbox.id);
    startPolling();
  };

  const handleCreate = async () => {
    clearError();
    await launchSandbox();
    startPolling();
  };

  const handleRetry = () => {
    clearError();
    fetchSandboxes();
  };

  /* ------------------------------------------------------------------ */
  /*  Render helpers                                                     */
  /* ------------------------------------------------------------------ */

  // Loading / checking state
  if (loading && !sandbox) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p className="text-sm text-muted-foreground">
            Checking workspace status…
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !sandbox) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-danger/10">
            <AlertTriangle className="h-7 w-7 text-danger" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            Something went wrong
          </h2>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="secondary" onClick={handleRetry}>
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // No sandbox exists → create prompt
  if (!sandbox) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Server className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            No workspace found
          </h2>
          <p className="text-sm text-muted-foreground">
            Launch a sandbox workspace to start coding with your AI agent. This
            creates an isolated development environment with OpenChamber.
          </p>
          <Button onClick={handleCreate} loading={loading}>
            <Rocket className="h-4 w-4" />
            Launch Workspace
          </Button>
        </div>
      </div>
    );
  }

  // Sandbox is sleeping → wake prompt
  if (sandbox.state === "sleeping") {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
            <Server className="h-7 w-7 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            Workspace is sleeping
          </h2>
          <p className="text-sm text-muted-foreground">
            Your sandbox workspace is hibernating to save resources. Wake it up
            to continue where you left off.
          </p>
          <Button onClick={handleWake} loading={loading}>
            <Rocket className="h-4 w-4" />
            Wake Workspace
          </Button>
        </div>
      </div>
    );
  }

  // Sandbox is in a transitional state (creating, waking, provisioning)
  if (TRANSITIONAL_STATES.has(sandbox.state)) {
    const elapsed = transitionStartRef.current
      ? Date.now() - transitionStartRef.current
      : 0;
    const TRANSITION_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

    if (elapsed > TRANSITION_TIMEOUT_MS) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="flex max-w-md flex-col items-center gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
              <AlertTriangle className="h-7 w-7 text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Workspace is taking longer than expected
            </h2>
            <p className="text-sm text-muted-foreground">
              Your sandbox has been in &quot;{sandbox.state}&quot; state for over 2 minutes.
              You can try creating a new workspace.
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleRetry}>
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
              <Button onClick={handleCreate} loading={loading}>
                <Rocket className="h-4 w-4" />
                New Workspace
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <Spinner size="lg" />
          <h2 className="text-xl font-semibold text-foreground">
            Starting your workspace…
          </h2>
          <p className="text-sm text-muted-foreground">
            {sandbox.state === "creating"
              ? "Provisioning a new sandbox container. This may take a minute."
              : "Waking up your sandbox. Almost there…"}
          </p>
        </div>
      </div>
    );
  }

  // Sandbox is in error state
  if (sandbox.state === "error") {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-danger/10">
            <AlertTriangle className="h-7 w-7 text-danger" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            Workspace error
          </h2>
          <p className="text-sm text-muted-foreground">
            Your sandbox encountered an error. Try restarting it or contact
            support if the issue persists.
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleRetry}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
            <Button onClick={handleWake} loading={loading}>
              <Rocket className="h-4 w-4" />
              Restart
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Sandbox is active → render full-page iframe with header bar
  // Use negative margins to counteract DashboardLayout's p-4/lg:p-6 padding
  return (
    <div className="-m-4 flex h-[calc(100%+2rem)] flex-col lg:-m-6 lg:h-[calc(100%+3rem)]">
      <div className="flex items-center justify-between border-b border-border bg-background/95 px-3 py-1.5">
        <span className="text-xs text-muted-foreground">
          OpenChamber Workspace
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const iframe = document.querySelector<HTMLIFrameElement>(
              'iframe[title="OpenChamber Workspace"]'
            );
            if (iframe) iframe.src = iframe.src;
          }}
          className="h-6 gap-1 px-2 text-xs"
        >
          <RefreshCw className="h-3 w-3" />
          Reload
        </Button>
      </div>
      <iframe
        src={OC_URL}
        title="OpenChamber Workspace"
        className="h-full w-full border-0"
        allow="clipboard-read; clipboard-write"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads"
      />
    </div>
  );
}
